import { Search, X } from "lucide-react";
import type React from "react";
import { forwardRef, useEffect, useRef, useState } from "react";

import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { Input } from "@/v2/ui/primitives/input";

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
  className?: string;
  debounce?: number;
}

const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  ({ value, onChange, placeholder, onClear, className, debounce }, ref) => {
    const defaultPlaceholder = strings("common.search");
    const [localValue, setLocalValue] = useState(value);
    const debounceTimerRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (debounce && debounce > 0) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          onChange(newValue);
        }, debounce);
      } else {
        onChange(newValue);
      }
    };

    const handleClear = () => {
      setLocalValue("");
      onChange("");
      if (onClear) {
        onClear();
      }
    };

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    return (
      <div className={cn("tw-relative tw-w-full", className)}>
        <Search className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-muted-foreground tw-pointer-events-none" />
        <Input
          ref={ref}
          type="text"
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder ?? defaultPlaceholder}
          className="tw-pl-9 tw-pr-9"
          data-testid={TEST_IDS.searchInput}
        />
        {localValue && (
          <button
            type="button"
            onClick={handleClear}
            className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors"
          >
            <X className="tw-h-4 tw-w-4" />
            <span className="tw-sr-only">{strings("common.clearSearch")}</span>
          </button>
        )}
      </div>
    );
  }
);
SearchBar.displayName = "SearchBar";

export { SearchBar };
